import {Action, DataStatus} from 'platform/components';
import {Hide, Show, Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';

import {useEffect, useState} from 'react';

import {isNil, isNotNil} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty, isPositive} from 'ramda-adjunct';

import {useGetCarVerticalConfigQuery, useGetTenantQuery} from '@dms/api/core';
import {useGetUserQuery} from '@dms/api/customer';
import {useCreateCarVerticalReportMutation, useGetCarVerticalReportsQuery} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {
  DEFAULT_COUNTRY,
  getNaturalPersonFullName,
  handleApiError,
  OlderReportRow,
  OlderReportsList,
  Widget,
} from '@dms/shared';

import {buildArray, parseDate, suffixTestId, TestIdProps} from 'shared/web';

import {CarVerticalLogo} from './CarVerticalLogo';

const POLLING_INTERVAL = 10000;

export interface CarVerticalWidgetProps extends TestIdProps {
  vehicleId?: string;
}

export function CarVerticalWidget(props: CarVerticalWidgetProps) {
  const formatDateTime = useDateTimeFormatter();
  const [pollingInterval, setPollingInterval] = useState<number | undefined>(undefined);

  const {data: tenant} = useGetTenantQuery();
  const country = tenant?.country ?? DEFAULT_COUNTRY;

  const {data: carVerticalConfig} = useGetCarVerticalConfigQuery();

  const {
    data: reports,
    isLoading: isReportsLoading,
    isError: isReportsError,
  } = useGetCarVerticalReportsQuery(
    {vehicleId: props.vehicleId!},
    {
      skip: isNil(props.vehicleId) || !carVerticalConfig?.enabled,
      pollingInterval,
    }
  );

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPollingInterval(
      reports?.some((report) => report.status === 'in_progress') ? POLLING_INTERVAL : undefined
    );
  }, [reports]);

  const [lastReport, ...olderReports] = reports ?? [];

  const {data: user} = useGetUserQuery(
    {id: lastReport?.createdBy ?? ''},
    {skip: isNilOrEmpty(lastReport?.createdBy) || !carVerticalConfig?.enabled}
  );
  const userFullName = getNaturalPersonFullName(user);

  const [createCarVerticalReport, {isLoading: isCarVerticalReportCreating}] =
    useCreateCarVerticalReportMutation();

  const getNewReport = () => {
    if (carVerticalConfig?.enabled && isNotNil(props.vehicleId)) {
      createCarVerticalReport({
        vehicleId: props.vehicleId,
        createCarVerticalReportRequestBody: {country},
      })
        .unwrap()
        .then((report) => {
          if (isNotNil(report.url)) {
            window.open(report.url, '_blank');
          }
        })
        .catch(handleApiError);
    }
  };

  if (!carVerticalConfig?.enabled) {
    return null;
  }

  return (
    <Widget.Card data-testid={suffixTestId('carVerticalWidget', props)}>
      <Widget.Header
        logo={<CarVerticalLogo />}
        title={i18n.t('page.integrations.labels.carVerticalTitle')}
        subtitle={
          <VStack spacing={1}>
            <Hide when={lastReport}>
              <Text size="xSmall" color="secondary">
                {i18n.t('page.integrations.labels.carVerticalNewReportDescription')}
              </Text>
            </Hide>
            <Show when={isNotNil(lastReport)}>
              <Text size="xSmall" color="tertiary">
                {`${i18n.t('page.integrations.labels.carVerticalLastReport')} ${
                  lastReport?.createdAt
                    ? formatDateTime('dateShort', parseDate(lastReport.createdAt))
                    : ''
                }`}
                {isNotNilOrEmpty(userFullName) ? `, ${userFullName}` : ''}
              </Text>
            </Show>
            <Show when={isNotNil(lastReport) && lastReport?.status === 'failed'}>
              <Text size="xSmall" color="danger">
                {i18n.t('page.integrations.labels.reportGenerationFailed')}
              </Text>
            </Show>
          </VStack>
        }
        actions={buildArray<Action>()
          .when(isNotNil(lastReport), {
            type: 'button',
            variant: 'secondary',
            rightIcon: 'action/launch',
            isLoading: lastReport?.status === 'in_progress',
            isDisabled: lastReport?.status === 'failed',
            onClick: () => isNotNil(lastReport?.url) && window.open(lastReport.url),
            title: i18n.t('page.integrations.labels.carVerticalViewReport'),
            'data-testid': suffixTestId('carVerticalWidget-viewReport', props),
          })
          .add({
            type: 'button',
            variant: 'secondary',
            isLoading: isCarVerticalReportCreating,
            onClick: getNewReport,
            title: i18n.t('page.integrations.labels.carVerticalNewReport'),
            'data-testid': suffixTestId('carVerticalWidget-newReport', props),
          })}
        data-testid={suffixTestId('carVerticalWidget', props)}
      />
      <Show when={isReportsLoading || isReportsError}>
        <Widget.Content data-testid={suffixTestId('carVerticalWidget', props)}>
          <DataStatus
            isLoading={isReportsLoading}
            isError={isReportsError}
            errorMessage={i18n.t('page.integrations.errors.carVerticalReportsLoadError')}
          />
        </Widget.Content>
      </Show>
      <Show when={isPositive(olderReports.length)}>
        <OlderReportsList
          title={i18n.t('page.integrations.labels.carVerticalPreviousReports')}
          data-testid={props['data-testid']}
        >
          {olderReports.map((report, index) => (
            <OlderReportRow
              key={report.id}
              href={report.url}
              title={i18n.t('page.integrations.labels.carVerticalPreviousReport', {
                date: formatDateTime('dateShort', parseDate(report.createdAt)),
              })}
              index={index}
              isDisabled={report.status !== 'finished'}
              createdBy={report.createdBy}
              data-testid={suffixTestId(`carVerticalWidget-previousReport-[${index}]`, props)}
            />
          ))}
        </OlderReportsList>
      </Show>
    </Widget.Card>
  );
}
